import { web3SignText, switchToChain } from 'utils/web3';
// @ts-ignore
import { getTransferMsgHash, getWithdrawMsgHash, sign } from '@debank/starkware-crypto-utils';
// @ts-ignore
import { getKeypairFromEthSignature } from '@debank/starkware-crypto-utils/src/js/key_derivation';
import { devLog, decrypt, numFormat, loginWallet, CHAINS_DICT } from '@/utils';
import { stringToHex } from 'web3-utils';
import {
  SETTLEMENT_ADDRESS,
  getL2ContractAddress,
  getL2DepositAddress,
  AccountStatus,
  L2_ABI,
  L2_BRIDGE_TOKEN_MAP,
} from '../consts';
import { ERC20Abi, USDTAbi } from '@/consts/overviewInfos';
import message from 'antd/es/message';
import { writeContract } from '@wagmi/core';

export const generateKey = async (address: string, nonce: number = 1, chainId: string = 'eth') => {
  await loginWallet();

  const chainText = chainId !== 'eth' ? ` on ${chainId}` : '';
  const rawText = `Please sign to confirm register DeBank L2 account nonce: ${nonce}${chainText}`;
  const sig = await web3SignText(rawText);

  const kp = getKeypairFromEthSignature(sig);
  devLog('generateKey', rawText, kp);
  const { publicKey, privateKey } = kp;
  return {
    publicKey,
    privateKey,
  };
};

export const register = async (publicKey: string, address: string, chainId?: string) => {
  await loginWallet();
  await switchToChain(chainId);
  const contractAddr = getL2ContractAddress(chainId);

  const config = CHAINS_DICT[chainId || 'eth'];

  const { hash: txhash } = await writeContract({
    mode: 'prepared',
    chainId: config.id,
    request: {
      address: contractAddr as any,
      abi: L2_ABI,
      args: [publicKey],
      functionName: 'register',
    },
  });

  devLog('register', txhash);

  return txhash;
};

export const depositTokenToL2Account = async (
  token: string,
  address: string,
  amount: number,
  chainId: string = 'eth',
  decimals?: number,
) => {
  return await transferRC20Token(
    token,
    address,
    getL2DepositAddress(chainId),
    amount,
    chainId,
    decimals,
  );
};

const transferRC20Token = async (
  tokenContractAddress: string,
  fromAddress: string,
  toAddress: string,
  amount: number,
  chainId: string,
  decimals?: number,
) => {
  await loginWallet();
  await switchToChain(chainId);

  if (typeof decimals !== 'number') {
    message.warning('decimals not found');
    return;
  }
  const precision = 10 ** decimals;

  const config = CHAINS_DICT[chainId];

  const isUSDT =
    L2_BRIDGE_TOKEN_MAP?.[chainId]?.USDT === tokenContractAddress ||
    L2_BRIDGE_TOKEN_MAP?.[chainId]?.USDt === tokenContractAddress;

  const { hash: txhash } = await writeContract({
    mode: 'prepared',
    chainId: config.id,
    request: {
      address: tokenContractAddress as any,
      //https://github.com/wagmi-dev/wagmi/issues/2749
      abi: JSON.parse(isUSDT ? USDTAbi : ERC20Abi),
      functionName: 'transfer',
      args: [toAddress as any, (amount * precision) as any],
    },
  });

  return txhash;
};

export const transferMessage = (price: number, receiverAddr: string) =>
  `Pay ${numFormat(price, 2)} USDC to send a message to ${receiverAddr}`;

export const getTransferMsgRawTextForUser = (
  senderAddr: string,
  receiverAddr: string,
  price: number,
  nextTxNonce: number,
) => {
  return `{
    action: {
      name: 'transfer',
      amount: ${(price * 10000).toFixed(0)},
      receiver: '${SETTLEMENT_ADDRESS}',
    },
    nonce: ${nextTxNonce},
    message: '${transferMessage(price, receiverAddr)}',
    user_addr: '${senderAddr}',
  }`;
};

export const getSignedTransferMessage = (
  senderAddr: string,
  receiverAddr: string,
  price: number,
  nextTxNonce: number,
  encryptedPrivateKey?: string,
  secret?: string,
  setSkipPassword?: boolean,
) => {
  return getL2SignedTransferMessage({
    message: transferMessage(price, receiverAddr),
    senderAddr,
    receiverAddr: SETTLEMENT_ADDRESS,
    price: price * 10000,
    nextTxNonce,
    encryptedPrivateKey,
    secret,
    setSkipPassword,
  });
};

export const getWithdrawTx = (
  senderAddr: string,
  fee: number,
  amount: number,
  nextTxNonce: number,
  encryptedPrivateKey: string,
  secret: string,
  chainId: string,
  tokenId: string,
  tokenSymbol: string = 'USDC',
) => {
  const totalAmount = amount + fee;
  const tx = {
    action: {
      name: 'withdraw',
      fee: Number((fee * 10000).toFixed(0)),
      amount: Number((amount * 10000).toFixed(0)),
    },
    nonce: nextTxNonce,
    message: stringToHex(
      `Withdrawal: ${numFormat(totalAmount, 2)} ${tokenSymbol}; Fee: ${numFormat(
        fee,
        2,
      )} ${tokenSymbol}; Receiving amount: ${numFormat(amount, 2)} ${tokenSymbol}`,
    ),
  };

  if (!secret) {
    return {
      ...tx,
      user_addr: senderAddr,
      chain_id: chainId,
      token_id: tokenId,
      r: '',
      s: '',
    };
  }

  const privateKey = decrypt(encryptedPrivateKey, secret);
  const hash = getWithdrawMsgHash(tx);

  const { r, s } = sign(privateKey, hash);

  return {
    ...tx,
    user_addr: senderAddr,
    r,
    s,
    chain_id: chainId,
    token_id: tokenId,
  };
};

export const getL2AccountStatus = (
  primaryL2PublicKey?: { public_key: string } | null,
  localPrimaryL2PublicKey?: string,
) => {
  if (primaryL2PublicKey === undefined) {
    return;
  }

  if (primaryL2PublicKey === null) {
    return AccountStatus.UNREGISTERED;
  }

  if (primaryL2PublicKey && !localPrimaryL2PublicKey) {
    return AccountStatus.NO_KEY;
  }

  if (primaryL2PublicKey?.public_key !== localPrimaryL2PublicKey) {
    return AccountStatus.INVALID_KEY;
  }
  return AccountStatus.NORMAL;
};

export type L2TxParams = {
  senderAddr: string;
  message: string;
  price: number;
  nextTxNonce: number;
  encryptedPrivateKey?: string;
  secret?: string;
  setSkipPassword?: boolean;
  receiverAddr: string;
};

export type L2TxReqParams = {
  action: {
    name: string;
    amount: number;
    receiver: string;
  };
  nonce: number;
  message: string;
  user_addr: string;
  r: string;
  s: string;
};

export const getL2SignedTransferMessage = ({
  senderAddr,
  message,
  price,
  nextTxNonce,
  encryptedPrivateKey,
  secret,
  setSkipPassword,
  receiverAddr,
}: L2TxParams): L2TxReqParams => {
  const transferMsg = {
    action: {
      name: 'transfer',
      amount: Number(price.toFixed(0)), // 美元数量 * 10000
      receiver: receiverAddr, // 结算地址
    },
    nonce: nextTxNonce, // transaction nonce
    message: stringToHex(message), // 注意 message 要转成 0x 字符串
  };

  const transferInfo: any = {
    ...transferMsg,
    user_addr: senderAddr,
  };

  if (!secret || !encryptedPrivateKey) {
    return {
      ...transferInfo,
      r: '',
      s: '',
    };
  }

  const privateKey = decrypt(encryptedPrivateKey, secret);

  const hash = getTransferMsgHash(transferMsg);

  const { r, s } = sign(privateKey, hash);

  if (setSkipPassword) {
    transferInfo['free_password'] = true;
  }

  devLog('getL2SignedTransferMessage', { r, s, hash, setSkipPassword, transferMsg });

  return {
    ...transferInfo,
    r,
    s,
  };
};

export const getL2TxRaw = ({
  senderAddr,
  receiverAddr,
  price,
  nextTxNonce,
  message,
}: {
  senderAddr: string;
  receiverAddr: string;
  price: number;
  nextTxNonce: number;
  message: string;
}) => {
  return `{
    action: {
      name: 'transfer',
      amount: ${price.toFixed(0)},
      receiver: ${receiverAddr},
    },
    nonce: ${nextTxNonce},
    message: '${message}',
    user_addr: '${senderAddr}',
  }`;
};
